/**
 * Hand tracking state, read once per XR frame from WebXR hand joints.
 *
 * Positions are in the XR reference space, i.e. the local space of the
 * player's XROrigin (rig). Controllers are ignored here — this module only
 * describes tracked hands, so controller play is untouched.
 */
import * as THREE from 'three';
import { FINGERS, type Finger } from './handGestures';
import { PinchFilter } from './pinch';

export type HandSide = 'left' | 'right';

export interface HandState {
  present: boolean;
  wrist: THREE.Vector3;
  /** Centre of the palm (middle-finger metacarpal). */
  palm: THREE.Vector3;
  /** Unit vector pointing out of the palm. */
  palmNormal: THREE.Vector3;
  indexTip: THREE.Vector3;
  thumbTip: THREE.Vector3;
  thumbBase: THREE.Vector3;
  /** Finger tips and knuckles (for gesture shapes). */
  tips: Record<Finger, THREE.Vector3>;
  proximals: Record<Finger, THREE.Vector3>;
  /** Pointing ray (WebXR target-ray space: shoulder → hand on Quest). */
  rayOrigin: THREE.Vector3;
  rayDir: THREE.Vector3;
  /** Raw pinch (WebXR select). Build tools read `pinch[side]` instead — it has hysteresis + a release grace. */
  pinching: boolean;
  pinchStart: number;
  inputSource: XRInputSource | null;
}

const fingerMap = () => Object.fromEntries(FINGERS.map((f) => [f, new THREE.Vector3()])) as Record<Finger, THREE.Vector3>;
const make = (): HandState => ({
  present: false,
  wrist: new THREE.Vector3(), palm: new THREE.Vector3(), palmNormal: new THREE.Vector3(0, -1, 0),
  indexTip: new THREE.Vector3(), thumbTip: new THREE.Vector3(), thumbBase: new THREE.Vector3(),
  tips: fingerMap(), proximals: fingerMap(),
  rayOrigin: new THREE.Vector3(), rayDir: new THREE.Vector3(0, 0, -1),
  pinching: false, pinchStart: 0, inputSource: null,
});

export const hands: Record<HandSide, HandState> = { left: make(), right: make() };
/**
 * The filtered pinch per hand (see pinch.ts), updated once per XR frame at the
 * end of updateHands. `pinch[side].held` is what build tools treat as "pinching".
 */
export const pinch: Record<HandSide, PinchFilter> = { left: new PinchFilter(), right: new PinchFilter() };
/** Head pose in the same (rig-local) space. */
export const handHead = { pos: new THREE.Vector3(0, 1.6, 0), quat: new THREE.Quaternion() };
/** True while at least one tracked hand (and no controller) drives input. */
export const handMode = { active: false };

/** How long a hand may drop out of tracking before its raw pinch is cleared
 *  (selectstart doesn't fire again for a pinch that was held through a blip). */
const LOST_CLEAR_MS = 300;
const lostSince: Record<HandSide, number> = { left: 0, right: 0 };

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _q = new THREE.Quaternion();

function jointPos(frame: XRFrame, hand: XRHand, name: XRHandJoint, ref: XRReferenceSpace, out: THREE.Vector3) {
  const space = hand.get(name);
  const pose = space && frame.getJointPose ? frame.getJointPose(space, ref) : null;
  if (!pose) return false;
  const p = pose.transform.position;
  out.set(p.x, p.y, p.z);
  return true;
}

export function updateHands(frame: XRFrame, ref: XRReferenceSpace) {
  const viewer = frame.getViewerPose(ref);
  if (viewer) {
    const p = viewer.transform.position, o = viewer.transform.orientation;
    handHead.pos.set(p.x, p.y, p.z);
    handHead.quat.set(o.x, o.y, o.z, o.w);
  }
  const seen: Record<HandSide, boolean> = { left: false, right: false };
  let controllers = 0;
  for (const src of frame.session.inputSources) {
    if (!src.hand) { if (src.gamepad && src.targetRayMode === 'tracked-pointer') controllers++; continue; }
    const side = src.handedness as HandSide;
    if (side !== 'left' && side !== 'right') continue;
    const h = hands[side];
    const ok = jointPos(frame, src.hand, 'wrist', ref, h.wrist)
      && jointPos(frame, src.hand, 'middle-finger-metacarpal', ref, h.palm)
      && jointPos(frame, src.hand, 'index-finger-tip', ref, h.indexTip)
      && jointPos(frame, src.hand, 'thumb-tip', ref, h.thumbTip);
    if (!ok) continue;
    jointPos(frame, src.hand, 'thumb-metacarpal', ref, h.thumbBase);
    for (const f of FINGERS) {
      jointPos(frame, src.hand, `${f}-finger-tip` as XRHandJoint, ref, h.tips[f]);
      jointPos(frame, src.hand, `${f}-finger-phalanx-proximal` as XRHandJoint, ref, h.proximals[f]);
    }
    // Palm normal from the wrist → index/pinky metacarpals; the cross
    // product's sign flips between hands.
    const idx = jointPos(frame, src.hand, 'index-finger-metacarpal', ref, _a);
    const pinky = jointPos(frame, src.hand, 'pinky-finger-metacarpal', ref, _b);
    if (idx && pinky) {
      _a.sub(h.wrist); _b.sub(h.wrist);
      h.palmNormal.crossVectors(_a, _b).normalize();
      if (side === 'left') h.palmNormal.negate();
    }
    const ray = frame.getPose(src.targetRaySpace, ref);
    if (ray) {
      const p = ray.transform.position, o = ray.transform.orientation;
      h.rayOrigin.set(p.x, p.y, p.z);
      _q.set(o.x, o.y, o.z, o.w);
      h.rayDir.set(0, 0, -1).applyQuaternion(_q).normalize();
    }
    h.inputSource = src;
    h.present = true;
    seen[side] = true;
  }
  const now = performance.now();
  for (const side of ['left', 'right'] as HandSide[]) {
    const h = hands[side];
    if (!seen[side]) {
      h.present = false; h.inputSource = null;
      if (!lostSince[side]) lostSince[side] = now;
      if (now - lostSince[side] > LOST_CLEAR_MS) h.pinching = false;
    } else lostSince[side] = 0;
    pinch[side].update(now, h.pinching, h.present ? h.indexTip.distanceTo(h.thumbTip) : null);
  }
  handMode.active = controllers === 0 && (seen.left || seen.right);
}

/** How much the palm faces the player's eyes (1 = straight at them). */
export function palmFacingHead(h: HandState) {
  _a.copy(handHead.pos).sub(h.palm).normalize();
  return h.palmNormal.dot(_a);
}

/** Wire WebXR select events (pinch) into the hand states. */
export function listenPinch(session: XRSession, onChange?: (side: HandSide, down: boolean) => void) {
  const start = (e: XRInputSourceEvent) => {
    const side = e.inputSource.handedness as HandSide;
    if (!e.inputSource.hand || (side !== 'left' && side !== 'right')) return;
    hands[side].pinching = true;
    hands[side].pinchStart = performance.now();
    onChange?.(side, true);
  };
  const end = (e: XRInputSourceEvent) => {
    const side = e.inputSource.handedness as HandSide;
    if (!e.inputSource.hand || (side !== 'left' && side !== 'right')) return;
    hands[side].pinching = false;
    onChange?.(side, false);
  };
  session.addEventListener('selectstart', start as EventListener);
  session.addEventListener('selectend', end as EventListener);
  return () => {
    session.removeEventListener('selectstart', start as EventListener);
    session.removeEventListener('selectend', end as EventListener);
  };
}

if (import.meta.env.DEV && typeof window !== 'undefined') (window as any).__gxHands = { hands, handHead, handMode, pinch };
if (import.meta.env.DEV && typeof window !== 'undefined') (window as any).__gxRaycaster = THREE.Raycaster;
if (import.meta.env.DEV && typeof window !== 'undefined') (window as any).__gxBox3 = THREE.Box3;
